<template>
	<div class="flex wrap xs-12">
		<div class="flex xs-12 xs-between xs-middle mb2">
			<div class="fs5 lh125 fw7 c1">{{ $t("status") }}</div>
			<j-button class="tertiary" @click="$modal.show('changeTemplate')">{{
				$t("change_template")
			}}</j-button>
		</div>

		<j-base-select
			v-model="status"
			:options="statusOptions"
			@input="updateStatus"
		/>

		<core-modal name="changeTemplate" :headline="$t('select_template')">
			<div class="p4 bg5">
				<j-base-select
					class="mr2"
					v-model="selectedTemplate"
					:options="templateOptions"
				/>
				<div class="flex xs-right xs-12 mt4">
					<j-button @click="changeSelectedTemplateId">{{
						$t("select")
					}}</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import JCard from "@/components/JCard"
import JTag from "@/components/JTag/"
import JButton from "@/components/JButton"
import JBaseSelect from "@/components/JBaseSelect"
import CoreModal from "@/modules/core/components/CoreModal"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JCard,
		JTag,
		JButton,
		JBaseSelect,
		CoreModal
	},

	data() {
		return {
			status: null,
			fetchedTemplates: null,
			selectedTemplate: null,
			caseStatusOptionsFact: null,
			defaultOptions: [
				{
					name: "PENDING",
					value: "PENDING"
				},
				{
					name: "SUBMITTED",
					value: "SUBMITTED"
				}
			]
		}
	},

	methods: {
		// fetch list of status templates from dynamic templates
		async fetchDynamicTemplates() {
			const query = gql`
				query fetchDynamicTemplates($where: templateWhereInput!) {
					templates(where: $where) {
						list {
							id
							name
							description
							... on dynamicTemplate {
								dynamicValues {
									valueId
									valueJsonString
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					type: "dynamic",
					id: "b1d50edf-3af8-4fdf-8ca8-51f4ee46e2c4" // hardcoded dynamicTemplate that will contain all checklists
				}
			}
			const res = await api({ query, variables })
			this.fetchedTemplates = res?.data?.templates?.list?.[0]?.dynamicValues?.map(
				item => JSON.parse(item?.valueJsonString)
			)
		},

		// fetch facts on fact to check if selectedStatusTemplate exists
		async fetchSelectedStatusTemplate() {
			const query = gql`
				query fetchSelectedStatusTemplate($where: caseWhere!) {
					cases(where: $where) {
						list {
							facts {
								id
								type
								value {
									stringValue
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					id: this.$route.params.id
				}
			}
			const res = await api({ query, variables })

			this.caseStatusOptionsFact = res?.data?.cases?.list?.[0]?.facts?.find(
				item => item?.type === "caseStatusOptions"
			)
		},

		// if case does not have a selected status template in fact we create one
		// otherwise we update existing selectedStatusTemplate fact
		async changeSelectedTemplateId() {
			if (!this.caseStatusOptionsFact) {
				const query = gql`
					mutation addFactToCase($caseId: String!, $input: addFactInput!) {
						addFactToCase(caseId: $caseId, input: $input) {
							status
							errors
						}
					}
				`
				const variables = {
					caseId: this.$route.params.id,
					input: {
						type: "caseStatusOptions",
						value: {
							stringValue: JSON.stringify(
								this.fetchedTemplates?.find(
									item => item.id === this.selectedTemplate
								)?.options
							)
						}
					}
				}
				const res = await api({ query, variables })
				if (res?.data?.addFactToCase?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text:
								res?.errors ||
								res?.data?.addFactToCase?.errors ||
								res?.data?.addFactToCase?.errors_2.message ||
								"Oops ! An error occured."
						}
					})
				} else
					this.$store.dispatch("addMessage", {
						message: {
							type: "success",
							text: this.$t("template_changed")
						}
					})
			} else {
				const query = gql`
					mutation updateFactOfCase(
						$caseId: String!
						$factId: String!
						$input: updateFactInput!
					) {
						updateFactOfCase(caseId: $caseId, factId: $factId, input: $input) {
							status
							errors
						}
					}
				`
				const variables = {
					caseId: this.$route.params.id,
					factId: this.caseStatusOptionsFact,
					input: {
						type: "selectedStatusTemplate",
						value: {
							stringValue: JSON.stringify(
								this.fetchedTemplates?.find(
									item => item.id === this.selectedTemplate
								)?.options
							)
						}
					}
				}
				const res = await api({ query, variables })
				if (res?.data?.updateFactOfCase?.status !== "success") {
					this.$store.dispatch("addMessage", {
						message: {
							type: "error",
							text:
								res?.errors ||
								res?.data?.updateFactOfCase?.errors ||
								res?.data?.updateFactOfCase?.errors_2.message ||
								"Oops ! An error occured."
						}
					})
				}
			}

			this.fetchSelectedStatusTemplate()
			this.$modal.hide("changeTemplate")
		},

		// update status of case
		async updateStatus() {
			const query = gql`
				mutation updateStatus($id: String!, $input: updateCaseInput!) {
					updateCase(id: $id, input: $input) {
						status
						errors
					}
				}
			`

			const variables = {
				id: this.$route.params.id,
				input: {
					status: this.status
				}
			}

			const res = await api({ query, variables })
			if (res?.data?.updateCase?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateCase?.errors ||
							res?.data?.updateCase?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("status_updated")
					}
				})
		}
	},

	computed: {
		statusOptions() {
			const stringified = this.caseStatusOptionsFact?.value?.stringValue
			return stringified ? JSON.parse(stringified) : this.defaultOptions
		},
		templateOptions() {
			return this.fetchedTemplates?.map(item => ({
				name: item.name,
				value: item.id
			}))
		}
	},

	created() {
		this.status = this.$store.state.caseManagement.case.status
		this.fetchDynamicTemplates()
		this.fetchSelectedStatusTemplate()
	}
}
</script>
